import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import { FaPlus, FaFilePdf, FaMagic, FaExternalLinkAlt } from 'react-icons/fa';
import axiosInstance from 'helpers/api';
import SimplePDFEditor from './SimplePDFEditor';
import InlinePDFEditor from './InlinePDFEditor';
import PDFFillerEditor from './PDFFillerEditor';

interface AddFillableFieldsModalProps {
  show: boolean;
  onHide: () => void;
  documentUrl: string;
  documentName: string;
  transactionData?: any;
  onFieldsAdded?: () => void;
}

interface FieldDefinition {
  name: string;
  type: 'text' | 'date' | 'checkbox' | 'signature';
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  page: number;
}

const AddFillableFieldsModal: React.FC<AddFillableFieldsModalProps> = ({
  show,
  onHide,
  documentUrl,
  documentName,
  transactionData: propsTransactionData,
  onFieldsAdded
}) => {
  const [fields, setFields] = useState<FieldDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showSimpleEditor, setShowSimpleEditor] = useState(false);
  const [showInlineEditor, setShowInlineEditor] = useState(false);
  const [showPDFFillerEditor, setShowPDFFillerEditor] = useState(false);
  const [transactionData, setTransactionData] = useState<any>(propsTransactionData);

  const commonFields: FieldDefinition[] = [
    { name: 'buyer_name', type: 'text', label: 'Buyer Name', x: 100, y: 650, width: 200, height: 20, page: 0 },
    { name: 'buyer_signature', type: 'signature', label: 'Buyer Signature', x: 100, y: 620, width: 200, height: 30, page: 0 },
    { name: 'seller_name', type: 'text', label: 'Seller Name', x: 350, y: 650, width: 200, height: 20, page: 0 },
    { name: 'seller_signature', type: 'signature', label: 'Seller Signature', x: 350, y: 620, width: 200, height: 30, page: 0 },
    { name: 'date_signed', type: 'date', label: 'Date', x: 450, y: 580, width: 100, height: 20, page: 0 },
    { name: 'property_address', type: 'text', label: 'Property Address', x: 100, y: 700, width: 400, height: 20, page: 0 },
    { name: 'purchase_price', type: 'text', label: 'Purchase Price', x: 100, y: 550, width: 150, height: 20, page: 0 }
  ];

  const addQuickFields = () => {
    setFields(commonFields);
  };

  const handleConvert = async () => {
    if (fields.length === 0) {
      setError('Please add at least one field before converting');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // First download the PDF from the URL (use proxy to avoid CORS)
      let pdfResponse;
      let pdfBlob;
      
      try {
        // Try direct download first
        pdfResponse = await fetch(documentUrl);
        pdfBlob = await pdfResponse.blob();
      } catch (corsError) {
        console.log('Direct fetch failed, trying proxy...', corsError);
        // Fall back to proxy endpoint
        const proxyUrl = `/pdf-proxy?url=${encodeURIComponent(documentUrl)}`;
        pdfResponse = await axiosInstance.get(proxyUrl, {
          responseType: 'blob'
        });
        pdfBlob = pdfResponse.data;
      }
      
      // Create form data
      const formData = new FormData();
      formData.append('pdf', pdfBlob, documentName);
      formData.append('fieldDefinitions', JSON.stringify(fields));

      // Convert to fillable
      const response = await axiosInstance.post('/fillable-forms/convert-to-fillable', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          onHide();
          if (onFieldsAdded) {
            onFieldsAdded();
          }
        }, 2000);
      }
    } catch (err) {
      console.error('Error converting PDF:', err);
      setError('Failed to convert PDF to fillable form');
    } finally {
      setLoading(false);
    }
  };

  if (showSimpleEditor) {
    return (
      <SimplePDFEditor
        show={showSimpleEditor}
        onHide={() => {
          setShowSimpleEditor(false);
          onHide();
        }}
        documentUrl={documentUrl}
        documentName={documentName}
        onComplete={onFieldsAdded}
      />
    );
  }

  // If PDFFiller editor is shown, render it instead
  if (showPDFFillerEditor) {
    return (
      <PDFFillerEditor
        show={showPDFFillerEditor}
        onHide={() => {
          setShowPDFFillerEditor(false);
          onHide();
        }}
        documentUrl={documentUrl}
        documentName={documentName}
        transactionData={transactionData}
        onSave={async (documentData) => {
          console.log('PDFFiller document saved:', documentData);
          if (onFieldsAdded) {
            onFieldsAdded();
          }
        }}
      />
    );
  }

  // If inline editor is shown, render it instead
  if (showInlineEditor) {
    return (
      <InlinePDFEditor
        show={showInlineEditor}
        onHide={() => {
          setShowInlineEditor(false);
          onHide();
        }}
        documentUrl={documentUrl}
        documentName={documentName}
        transactionData={transactionData}
        onSave={async (pdfBytes) => {
          // Handle the saved PDF bytes
          console.log('PDF saved with', pdfBytes.length, 'bytes');
          if (onFieldsAdded) {
            onFieldsAdded();
          }
        }}
      />
    );
  }

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaFilePdf className="me-2" />
          Make PDF Fillable: {documentName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && (
          <Alert variant="success">
            PDF converted to fillable form successfully! You can now use it as a template.
          </Alert>
        )}

        {/* PDFFiller Editor Button - Prominent */}
        <div className="text-center mb-4">
          <Button
            variant="primary"
            size="lg"
            onClick={() => setShowPDFFillerEditor(true)}
            className="px-5"
          >
            <FaFilePdf className="me-2" />
            Open PDF Editor (PDFFiller)
          </Button>
          <div className="text-muted small mt-2">
            Professional PDF editing - Type directly into your PDF like Adobe!
          </div>
        </div>
        
        {/* Alternative Inline Editor */}
        <div className="text-center mb-3">
          <Button
            variant="outline-primary"
            onClick={() => setShowInlineEditor(true)}
          >
            <FaMagic className="me-2" />
            Use Basic Editor (Free)
          </Button>
        </div>

        <hr className="my-4" />

        <Alert variant="info">
          <strong>Alternative Options:</strong> If the editor doesn't work with your PDF, try our{' '}
          <Button 
            variant="link" 
            className="p-0 text-decoration-underline"
            onClick={() => setShowSimpleEditor(true)}
          >
            <FaExternalLinkAlt className="me-1" />
            External Editor Options
          </Button>
          {' '}for complex PDFs.
        </Alert>
        
        <Alert variant="warning">
          <strong>Note:</strong> The built-in editor may have issues with complex PDFs. 
          For best results, use the external editor options.
        </Alert>

        <div className="mb-3">
          <Button
            variant="primary"
            onClick={addQuickFields}
            disabled={fields.length > 0}
          >
            <FaMagic className="me-2" />
            Add Common Contract Fields
          </Button>
        </div>

        {fields.length > 0 && (
          <>
            <h6>Fields to Add:</h6>
            <Form>
              {fields.map((field, index) => (
                <Row key={index} className="mb-2 align-items-center">
                  <Col md={3}>
                    <Form.Text>{field.label}</Form.Text>
                  </Col>
                  <Col md={2}>
                    <Form.Text className="text-muted">{field.type}</Form.Text>
                  </Col>
                  <Col md={3}>
                    <Form.Text className="text-muted">
                      Position: ({field.x}, {field.y})
                    </Form.Text>
                  </Col>
                  <Col md={2}>
                    <Form.Text className="text-muted">
                      Size: {field.width}x{field.height}
                    </Form.Text>
                  </Col>
                  <Col md={2}>
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger p-0"
                      onClick={() => setFields(fields.filter((_, i) => i !== index))}
                    >
                      Remove
                    </Button>
                  </Col>
                </Row>
              ))}
            </Form>

            <Alert variant="warning" className="mt-3">
              <small>
                <strong>Note:</strong> Field positions are estimates. You may need to adjust them 
                after conversion based on your specific document layout.
              </small>
            </Alert>
          </>
        )}

        <hr />

        <h6>Add Custom Field:</h6>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            const form = e.target as HTMLFormElement;
            const newField: FieldDefinition = {
              name: (form.elements.namedItem('fieldName') as HTMLInputElement).value,
              label: (form.elements.namedItem('fieldLabel') as HTMLInputElement).value,
              type: (form.elements.namedItem('fieldType') as HTMLSelectElement).value as any,
              x: 100,
              y: 500 - (fields.length * 30),
              width: 200,
              height: 20,
              page: 0
            };
            setFields([...fields, newField]);
            form.reset();
          }}
        >
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Field Name</Form.Label>
                <Form.Control
                  name="fieldName"
                  type="text"
                  placeholder="e.g., client_email"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Label</Form.Label>
                <Form.Control
                  name="fieldLabel"
                  type="text"
                  placeholder="e.g., Client Email"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Type</Form.Label>
                <Form.Select name="fieldType" required>
                  <option value="text">Text</option>
                  <option value="date">Date</option>
                  <option value="checkbox">Checkbox</option>
                  <option value="signature">Signature</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={1} className="d-flex align-items-end">
              <Button type="submit" variant="success">
                <FaPlus />
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleConvert}
          disabled={loading || fields.length === 0 || success}
        >
          {loading ? 'Converting...' : 'Convert to Fillable PDF'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddFillableFieldsModal;